import { X } from "lucide-react";
import { useState, useEffect } from "react";

const CalmDownTimer = ({ isOpen, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedTime, setSelectedTime] = useState(60);

  useEffect(() => {
    if (!isOpen) {
      setIsRunning(false);
      setTimeLeft(selectedTime);
      return;
    }
  }, [isOpen, selectedTime]);

  useEffect(() => {
    if (!isRunning || timeLeft <= 0) {
      if (timeLeft <= 0) setIsRunning(false);
      return;
    }
    const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timer);
  }, [isRunning, timeLeft]);

  const startTimer = (seconds) => {
    setSelectedTime(seconds);
    setTimeLeft(seconds);
    setIsRunning(true);
  };

  const progress = selectedTime > 0 ? ((selectedTime - timeLeft) / selectedTime) * 100 : 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="calm-timer-container" onClick={onClose}>
      <div className="bg-[#FDFBF7] rounded-3xl p-8 sm:p-12 max-w-md w-full text-center shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#6B6B6B] hover:text-[#4A4A4A] transition-colors" data-testid="calm-timer-close">
          <X className="w-8 h-8" />
        </button>
        
        <h2 className="text-2xl sm:text-3xl font-bold text-[#4A4A4A] mb-4">
          Let's calm down
        </h2>
        <p className="text-sm text-[#6B6B6B] mb-6">Take a moment. You're safe here.</p>

        {/* Timer Circle */}
        <div className="relative w-44 h-44 sm:w-52 sm:h-52 mx-auto mb-6">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="44" fill="none" stroke="#E8E5DC" strokeWidth="6" />
            <circle cx="50" cy="50" r="44" fill="none" stroke={timeLeft <= 0 ? '#5A8A5A' : '#4A7AA5'} strokeWidth="6" 
              strokeDasharray={`${2 * Math.PI * 44}`} 
              strokeDashoffset={`${2 * Math.PI * 44 * (1 - progress / 100)}`}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-linear" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {timeLeft <= 0 ? (
              <span className="text-3xl">💛</span>
            ) : (
              <span className="text-4xl sm:text-5xl font-bold text-[#4A4A4A]">{timeLeft}</span>
            )}
            <span className="text-xs text-[#9A9A9A] mt-1">{timeLeft <= 0 ? 'Well done!' : 'seconds'}</span>
          </div>
        </div>

        {/* Soothing color wave */}
        {isRunning && (
          <div className="h-2 rounded-full bg-gradient-to-r from-[#E6F0F9] via-[#FFF7D6] to-[#E8F5E9] mb-6 animate-pulse"></div>
        )}

        {/* Time Options */}
        {!isRunning && timeLeft === selectedTime && (
          <div className="flex justify-center gap-3 mb-4">
            {[30, 60, 90].map(s => (
              <button key={s} onClick={() => startTimer(s)}
                className="px-5 py-2.5 rounded-full bg-[#E6F0F9] text-[#4A7AA5] font-bold text-sm hover:bg-[#C9DFE9] transition-all hover:scale-105">
                {s}s
              </button>
            ))}
          </div>
        )}

        <p className="text-base text-[#6B6B6B]">
          {timeLeft <= 0 ? "You did amazing! You are so brave 💛" : isRunning ? "Close your eyes and breathe slowly..." : "Pick how long you'd like to rest"}
        </p>
      </div>
    </div>
  );
};

export default CalmDownTimer;
